import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Globe, Save, Upload, Trash2, RefreshCw, Copy, ExternalLink, MessageSquareWarning } from 'lucide-react';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BusinessSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const [basicInfo, setBasicInfo] = useState({
    business_name: '',
    contact_number: '',
    email: '',
    address: '',
    country: 'IN',
    state: '',
    district: '',
    city: '',
    area: '',
    pincode: '',
    alternate_contact: '',
    tax_registration_number: '',
    logo_url: null
  });

  const [generalSettings, setGeneralSettings] = useState({
    currency: 'INR',
    timezone: 'Asia/Kolkata'
  });

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-settings/');
      if (response.data) {
        if (response.data.basic_info) {
          setBasicInfo(response.data.basic_info);
          setLogoPreview(response.data.basic_info.logo_url);
        }
        if (response.data.general_settings) {
          setGeneralSettings(response.data.general_settings);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await api.get('/business-settings/countries');
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  const fetchStates = useCallback(async (countryCode) => {
    try {
      const response = await api.get(`/business-settings/states/${countryCode}`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await api.get('/business-settings/currencies');
      setCurrencies(response.data);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  }, []);

  const fetchTimezones = useCallback(async () => {
    try {
      const response = await api.get('/business-settings/timezones');
      setTimezones(response.data);
    } catch (error) {
      console.error('Error fetching timezones:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchCountries();
    fetchCurrencies();
    fetchTimezones();
  }, [fetchSettings, fetchCountries, fetchCurrencies, fetchTimezones]);

  useEffect(() => {
    if (basicInfo.country) {
      fetchStates(basicInfo.country);
    }
  }, [basicInfo.country, fetchStates]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size should be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await api.delete('/business-settings/logo');
      setLogoPreview(null);
      setLogoFile(null);
      setBasicInfo(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo deleted successfully');
    } catch (error) {
      toast.error('Failed to delete logo');
    }
  };

  const handleSaveBasicInfo = async () => {
    if (!basicInfo.business_name || !basicInfo.contact_number || !basicInfo.email) {
      toast.error('Business Name, Contact Number, and Email are required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('business_name', basicInfo.business_name);
      formData.append('contact_number', basicInfo.contact_number);
      formData.append('email', basicInfo.email);
      formData.append('address', basicInfo.address || '');
      formData.append('country', basicInfo.country || 'IN');
      formData.append('state', basicInfo.state || '');
      formData.append('district', basicInfo.district || '');
      formData.append('city', basicInfo.city || '');
      formData.append('area', basicInfo.area || '');
      formData.append('pincode', basicInfo.pincode || '');
      formData.append('alternate_contact', basicInfo.alternate_contact || '');
      formData.append('tax_registration_number', basicInfo.tax_registration_number || '');
      
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      await fetch(`${API_URL}/api/business-settings/basic-info`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      toast.success('Basic information saved successfully');
      setLogoFile(null);
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save basic information');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGeneralSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/business-settings/general-settings?currency=${generalSettings.currency}&timezone=${generalSettings.timezone}`);
      toast.success('General settings saved successfully');
    } catch (error) {
      toast.error('Failed to save general settings');
    } finally {
      setSaving(false);
    }
  };

  const complaintFormUrl = `${window.location.origin}/complaint`;
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
          <p className="text-gray-600">Configure your business information and preferences</p>
        </div>
        <Button variant="outline" onClick={fetchSettings}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Complaint Form URL Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquareWarning className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800 mb-1">Customer Complaint Form URL</p>
                <p className="text-blue-600 font-mono text-sm">{complaintFormUrl}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(complaintFormUrl)}>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(complaintFormUrl, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="basic" className="space-y-4">
        <TabsList>
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Basic Information
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            General Settings
          </TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter your registered business details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-2">
                <Label>Business Logo</Label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Business Logo"
                        className="w-24 h-24 object-contain border rounded-lg"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                        onClick={handleDeleteLogo}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                      <Building2 className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="w-64"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max file size: 2MB. Supported: JPG, PNG, GIF</p>
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name">Registered Business Name *</Label>
                  <Input
                    id="business_name"
                    value={basicInfo.business_name}
                    onChange={(e) => setBasicInfo({ ...basicInfo, business_name: e.target.value })}
                    placeholder="Enter business name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax_registration_number">TAX Registration Number (GST)</Label>
                  <Input
                    id="tax_registration_number"
                    value={basicInfo.tax_registration_number}
                    onChange={(e) => setBasicInfo({ ...basicInfo, tax_registration_number: e.target.value })}
                    placeholder="e.g., 22AAAAA0000A1Z5"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_number">Contact Number *</Label>
                  <Input
                    id="contact_number"
                    value={basicInfo.contact_number}
                    onChange={(e) => setBasicInfo({ ...basicInfo, contact_number: e.target.value })}
                    placeholder="e.g., +91 98765 43210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alternate_contact">Alternate Contact Number</Label>
                  <Input
                    id="alternate_contact"
                    value={basicInfo.alternate_contact}
                    onChange={(e) => setBasicInfo({ ...basicInfo, alternate_contact: e.target.value })}
                    placeholder="e.g., +91 98765 43211"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={basicInfo.email}
                    onChange={(e) => setBasicInfo({ ...basicInfo, email: e.target.value })}
                    placeholder="e.g., contact@business.com"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={basicInfo.address}
                  onChange={(e) => setBasicInfo({ ...basicInfo, address: e.target.value })}
                  placeholder="Enter full address"
                  rows={2}
                />
              </div>

              {/* Location Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={basicInfo.country}
                    onValueChange={(value) => setBasicInfo({ ...basicInfo, country: value, state: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={basicInfo.state}
                    onValueChange={(value) => setBasicInfo({ ...basicInfo, state: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">District</Label>
                  <Input
                    id="district"
                    value={basicInfo.district}
                    onChange={(e) => setBasicInfo({ ...basicInfo, district: e.target.value })}
                    placeholder="Enter district"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={basicInfo.city}
                    onChange={(e) => setBasicInfo({ ...basicInfo, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area">Area</Label>
                  <Input
                    id="area"
                    value={basicInfo.area}
                    onChange={(e) => setBasicInfo({ ...basicInfo, area: e.target.value })}
                    placeholder="Enter area/locality"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    value={basicInfo.pincode}
                    onChange={(e) => setBasicInfo({ ...basicInfo, pincode: e.target.value })}
                    placeholder="e.g., 600001"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveBasicInfo} disabled={saving}>
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Basic Information
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Configure currency and timezone preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={generalSettings.currency}
                    onValueChange={(value) => setGeneralSettings({ ...generalSettings, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.symbol} - {currency.name} ({currency.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">This will be used for invoices and financial reports</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Time Zone</Label>
                  <Select
                    value={generalSettings.timezone}
                    onValueChange={(value) => setGeneralSettings({ ...generalSettings, timezone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.map((tz) => (
                        <SelectItem key={tz.code} value={tz.code}>
                          {tz.name} (UTC{tz.offset})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">All dates and times will be displayed in this timezone</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveGeneralSettings} disabled={saving}>
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save General Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BusinessSettings;
